import React, { useState } from 'react';
import { X, Upload, FileText, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ImportFundedDealsModal = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Preview, 4: Import
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    business_name: '',
    company: '',
    funding_amount: '',
    payback_amount: '',
    term: '',
    payment_frequency: '',
    payment_amount: '',
    funding_date: '',
    first_payment_date: '',
    notes: ''
  });

  const requiredFields = [
    { key: 'business_name', label: 'Business', required: true },
    { key: 'company', label: 'Company', required: false },
    { key: 'funding_amount', label: 'Advanced $', required: true },
    { key: 'payback_amount', label: 'Payback $', required: true },
    { key: 'term', label: 'Term/F (e.g., 110/D or 24/W)', required: true },
    { key: 'payment_frequency', label: 'Payment Frequency (daily/weekly)', required: true },
    { key: 'payment_amount', label: 'Payment Amount', required: true },
    { key: 'funding_date', label: 'Date (YYYY-MM-DD)', required: true },
    { key: 'first_payment_date', label: 'First Payment Date (YYYY-MM-DD)', required: true },
    { key: 'notes', label: 'Notes', required: false }
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast({ title: 'Error', description: 'Please select a CSV file', variant: 'destructive' });
        return;
      }
      setFile(selectedFile);
      
      // Parse CSV to extract headers and preview data
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast({ title: 'Error', description: 'CSV file is empty or invalid', variant: 'destructive' });
          return;
        }
        
        // Extract headers
        const headers = lines[0].split(',').map(h => h.trim().replace(/['"]/g, ''));
        setCsvHeaders(headers);
        
        // Extract first 3 data rows for preview
        const dataRows = lines.slice(1, 4).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/['"]/g, ''));
          const rowObj = {};
          headers.forEach((header, idx) => {
            rowObj[header] = values[idx] || '';
          });
          return rowObj;
        });
        setCsvData(dataRows);
        
        // Auto-detect column mapping (exact match or close match)
        const autoMapping = {};
        requiredFields.forEach(field => {
          const exactMatch = headers.find(h => h.toLowerCase() === field.label.toLowerCase().replace(/\s*\(.*/g, ''));
          const partialMatch = headers.find(h => 
            h.toLowerCase().includes(field.key.replace(/_/g, ' ').toLowerCase()) ||
            field.key.replace(/_/g, ' ').toLowerCase().includes(h.toLowerCase())
          );
          autoMapping[field.key] = exactMatch || partialMatch || '';
        });
        setColumnMapping(autoMapping);
        
        setStep(2); // Move to mapping step
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleMappingChange = (fieldKey, csvColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [fieldKey]: csvColumn
    }));
  };

  const validateMapping = () => {
    const missingRequired = requiredFields
      .filter(field => field.required && !columnMapping[field.key])
      .map(field => field.label);
    
    if (missingRequired.length > 0) {
      toast({ 
        title: 'Incomplete Mapping', 
        description: `Please map: ${missingRequired.join(', ')}`,
        variant: 'destructive' 
      });
      return false;
    }
    return true;
  };

  const handlePreview = () => {
    if (validateMapping()) {
      setStep(3);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(columnMapping));

      const token = localStorage.getItem('mcaToken');
      const response = await axios.post(`${API_BASE}/deals/import-funded`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({ 
        title: 'Success', 
        description: `Imported ${response.data.imported} deals successfully` 
      });
      
      // Reset state
      setFile(null);
      setCsvHeaders([]);
      setCsvData([]);
      setStep(1);
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({ 
        title: 'Import Failed', 
        description: error.response?.data?.detail || 'Failed to import deals',
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `Date,Business,Company,Advanced $,Payback $,Weekly $,Daily $,Term/F,Net $,Collected $,Paid Off %,Remaining $,Status,Payment Frequency,Payment Amount,First Payment Date,Notes
2026-01-15,ACME Corporation,ACME Inc,50000,62500,0,568.18,110/D,50000,0,0,62500,Current,daily,568.18,2026-01-16,Test deal
2026-01-10,XYZ Industries,XYZ Co,75000,93750,3906.25,0,24/W,75000,0,0,93750,Current,weekly,3906.25,2026-01-11,Another test`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'funded_deals_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getMappedPreview = () => {
    return csvData.map(row => {
      const mappedRow = {};
      Object.keys(columnMapping).forEach(fieldKey => {
        const csvColumn = columnMapping[fieldKey];
        mappedRow[fieldKey] = csvColumn ? row[csvColumn] : '';
      });
      return mappedRow;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Upload className="text-blue-600" size={28} />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Import Funded Deals</h2>
              <p className="text-sm text-gray-500">Step {step} of 3</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Step 1: Upload CSV */}
        {step === 1 && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-blue-600 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Instructions:</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Upload a CSV file with your funded deals</li>
                    <li>• Column headers can be in any format (you'll map them in the next step)</li>
                    <li>• Download the template below if you need a reference</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <Button
                onClick={downloadTemplate}
                variant="outline"
                className="w-full border-dashed border-2 border-gray-300 hover:border-blue-500"
              >
                <FileText size={18} className="mr-2" />
                Download CSV Template
              </Button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Upload CSV File *</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full border rounded p-2"
              />
            </div>
          </>
        )}

        {/* Step 2: Map Columns */}
        {step === 2 && (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-green-600 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-green-900 mb-2">Map Your Columns:</h3>
                  <p className="text-sm text-green-800">
                    We detected these columns in your CSV: <strong>{csvHeaders.join(', ')}</strong>
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    Please select which column matches each required field below.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              {requiredFields.map(field => (
                <div key={field.key} className="grid grid-cols-2 gap-4 items-center">
                  <label className="text-sm font-medium text-gray-700">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={columnMapping[field.key]}
                    onChange={(e) => handleMappingChange(field.key, e.target.value)}
                    className="border rounded px-3 py-2 text-sm"
                  >
                    <option value="">-- Select Column --</option>
                    {csvHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handlePreview}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Preview Data <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-purple-600 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-purple-900 mb-2">Preview Mapped Data:</h3>
                  <p className="text-sm text-purple-800">
                    Review the first 3 rows below. Click "Import Deals" to proceed.
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-xs border">
                <thead>
                  <tr className="bg-gray-100">
                    {requiredFields.filter(f => columnMapping[f.key]).map(field => (
                      <th key={field.key} className="px-2 py-2 text-left border">{field.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getMappedPreview().map((row, idx) => (
                    <tr key={idx} className="border-t">
                      {requiredFields.filter(f => columnMapping[f.key]).map(field => (
                        <td key={field.key} className="px-2 py-2 border">{row[field.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(2)}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Back to Mapping
              </Button>
              <Button
                onClick={handleImport}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Importing...' : 'Import Deals'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportFundedDealsModal;
